import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/ui/combobox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";
import { fmtEur, fmtDate, monthLabel } from "@/lib/format";
import { FileDown, X, ArrowDownCircle, Split, Mail } from "lucide-react";
import { toast } from "sonner";

const PSTAT = { paid_ontime: "Betaald", paid_late: "Betaald (te laat)", partial: "Deels betaald", open: "Openstaand", late: "Te laat" };
const KIND_LABEL = { huur: "Huur", lening: "Lening", factuur: "Factuur" };

export default function StatementDialog({ open, onOpenChange, tenantId, contracts = [] }) {
  const [sel, setSel] = useState("all");
  const [data, setData] = useState(null);

  const options = [
    { v: "all", l: "Alle contracten" },
    ...(contracts || []).map((c) => ({ v: c.id, l: `${c.ovk_number || KIND_LABEL[c.kind] || "Contract"}${c.ovk_number ? ` · ${KIND_LABEL[c.kind] || ""}` : ""}` })),
    { v: "factoring", l: "Factoring" },
  ];

  useEffect(() => { if (open) setSel("all"); }, [open]);
  useEffect(() => {
    if (open && tenantId) api.get(`/tenants/${tenantId}/statement?sel=${sel}`).then((r) => setData(r.data)).catch(() => {});
  }, [open, tenantId, sel]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/tenants/${tenantId}/statement/pdf?sel=${sel}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `betalingsoverzicht_${sel}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Overzicht gedownload");
    } catch { toast.error("PDF mislukt"); }
  };

  const [emailing, setEmailing] = useState(false);
  const emailToDebtor = async () => {
    setEmailing(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/statement/email?sel=${sel}`);
      toast.success(`Overzicht gemaild naar ${(r.data.recipients || []).join(", ")}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "E-mailen mislukt"); }
    finally { setEmailing(false); }
  };

  const t = data?.totals;
  const receipts = data?.receipts || [];
  const remainders = data?.remainders || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 max-h-[92vh] flex flex-col overflow-hidden">
        <DialogTitle className="sr-only">Betalingsoverzicht</DialogTitle>
        <DialogDescription className="sr-only">Totaal betalingsoverzicht per contract met ontvangen betalingen en restbedragen.</DialogDescription>
        <div className="flex items-center justify-between p-4 border-b bg-slate-50">
          <div>
            <h3 className="font-heading font-bold text-slate-900">Totaal betalingsoverzicht</h3>
            <p className="text-xs text-slate-500">Termijnen, ontvangen betalingen, boetes en restbedragen</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-56"><Combobox testId="statement-kind-select" value={sel} onChange={setSel} searchPlaceholder="Zoek…" options={options.map((o) => ({ value: o.v, label: o.l }))} /></div>
            <button onClick={() => onOpenChange(false)}><X className="h-5 w-5 text-slate-500" /></button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-5">
          {/* Termijnen */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 mb-2">Termijnen</h4>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Overeenkomst</TableHead><TableHead>Periode</TableHead><TableHead>Vervaldatum</TableHead><TableHead>Bedrag</TableHead>
                <TableHead>Boete</TableHead><TableHead>Totaal</TableHead><TableHead>Ontvangen</TableHead><TableHead>Status</TableHead><TableHead>Betaald op</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(data?.rows || []).map((r, i) => (
                  <TableRow key={i} data-testid={`statement-row-${i}`}>
                    <TableCell className="font-mono text-xs text-slate-500">{r.ovk || "—"}</TableCell>
                    <TableCell className="font-medium">{monthLabel(r.period)}</TableCell>
                    <TableCell>{fmtDate(r.due_date)}</TableCell>
                    <TableCell>{fmtEur(r.amount)}</TableCell>
                    <TableCell className={r.penalty > 0 ? "text-red-600" : ""}>{r.penalty > 0 ? fmtEur(r.penalty) : "—"}</TableCell>
                    <TableCell>{fmtEur(r.due_total)}</TableCell>
                    <TableCell className={r.paid > 0 ? "text-emerald-600 font-medium" : "text-slate-400"}>{r.paid > 0 ? fmtEur(r.paid) : "—"}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{PSTAT[r.status] || r.status}</Badge>
                      {r.days_late > 0 && <span className="text-xs text-red-600 ml-1" data-testid={`statement-dayslate-${i}`}>({r.days_late} dgn)</span>}
                      {r.online && (
                        <div className="text-[11px] mt-0.5" data-testid={`statement-online-${i}`}>
                          {r.online.status === "paid"
                            ? <span className="text-emerald-600">● Online betaald {r.online.date ? fmtDate(r.online.date) : ""} ({r.online.method || "iDEAL"})</span>
                            : <span className="text-amber-600">● Online betaling bezig ({r.online.method || "iDEAL"})</span>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{r.paid_date ? fmtDate(r.paid_date) : "—"}</TableCell>
                  </TableRow>
                ))}
                {(data?.rows || []).length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-6">Geen termijnen voor deze selectie.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>

          {/* Ontvangen betalingen */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><ArrowDownCircle className="h-3.5 w-3.5" /> Ontvangen betalingen</h4>
            {receipts.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="statement-no-receipts">Nog geen betalingen ontvangen.</p>
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>Datum</TableHead><TableHead>Termijn</TableHead><TableHead>Overeenkomst</TableHead><TableHead>Bedrag</TableHead><TableHead>Soort</TableHead></TableRow></TableHeader>
                <TableBody>
                  {receipts.map((r, i) => (
                    <TableRow key={i} data-testid={`statement-receipt-${i}`}>
                      <TableCell>{fmtDate(r.date)}</TableCell>
                      <TableCell>{monthLabel(r.period)}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{r.ovk || "—"}</TableCell>
                      <TableCell className="text-emerald-600 font-medium">{fmtEur(r.amount)}</TableCell>
                      <TableCell><Badge variant="outline">{r.kind === "vooruit" ? "Vooruitbetaling" : "Betaling"}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Restbedragen */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><Split className="h-3.5 w-3.5" /> Restbedragen</h4>
            {remainders.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="statement-no-remainders">Geen restbedragen verwerkt.</p>
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>Datum</TableHead><TableHead>Termijn</TableHead><TableHead>Bedrag</TableHead><TableHead>Verwerking</TableHead></TableRow></TableHeader>
                <TableBody>
                  {remainders.map((r, i) => (
                    <TableRow key={i} data-testid={`statement-remainder-${i}`}>
                      <TableCell>{fmtDate(r.date)}</TableCell>
                      <TableCell>{monthLabel(r.period)}</TableCell>
                      <TableCell className="text-amber-700 font-medium">{fmtEur(r.amount)}</TableCell>
                      <TableCell className="text-sm text-slate-600">{r.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {t && (
            <div className="bg-slate-50 border rounded-xl p-4 grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm" data-testid="statement-totals">
              <div><div className="text-xs text-slate-400">In rekening</div><div className="font-bold">{fmtEur(t.charged)}</div></div>
              <div><div className="text-xs text-slate-400">Boete</div><div className="font-bold text-red-600">{fmtEur(t.penalty)}</div></div>
              <div><div className="text-xs text-slate-400">Totaal</div><div className="font-bold">{fmtEur(t.total_due)}</div></div>
              <div><div className="text-xs text-slate-400">Betaald</div><div className="font-bold text-emerald-600">{fmtEur(t.paid)}</div></div>
              <div><div className="text-xs text-slate-400">Openstaand</div><div className="font-bold text-slate-800">{fmtEur(t.outstanding)}</div></div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t bg-slate-50">
          <Button variant="outline" onClick={emailToDebtor} disabled={emailing} data-testid="statement-email-button"><Mail className="h-4 w-4 mr-2" /> {emailing ? "Versturen…" : "E-mail naar debiteur"}</Button>
          <Button className="bg-blue-900 hover:bg-blue-800" onClick={downloadPdf} data-testid="statement-pdf-button"><FileDown className="h-4 w-4 mr-2" /> Download PDF</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
